/**
 * Billing Settings Page — /office/settings/billing/
 *
 * Behaviours (kept out of the template for CSP / maintenance):
 *   - Top-level section tabs (Documents / Billing rules / Emails / Bank
 *     accounts) with ?tab= persistence and open-on-error.
 *   - Per-field auto-save for the atomic controls (the three automation
 *     toggles + the rounding-increment select). Typed/validated fields keep
 *     the explicit Save button. ``billing_language`` is intentionally NOT
 *     auto-saved (it repopulates the explicitly-saved email templates).
 *   - Unsaved-changes guard for the explicit-save fields, surfaced through the
 *     global ``window.showConfirmModal`` via ``window._formDirty`` (boosted
 *     nav) plus a ``beforeunload`` safety net (hard nav).
 *   - Email template sub-tabs (invoice / credit note / reminder),
 *     "Reset to default", and locale-aware default loading.
 *   - Live prefix previews (e.g. "BC-2026-00001").
 *
 * All DOM entry points are looked up inside a root container with
 * `data-auto-init="billingSettings"` so the page is safe against HTMX swaps.
 */

var ACTIVE_TAB_CLASS = 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400';
var INACTIVE_TAB_CLASS = 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300';

var SETTINGS_TABS = ['documents', 'rules', 'emails', 'bank'];

// Module-level so the global htmx:confirm guard (app.js) can read the live
// state via ``window._formDirty``. Reset on every init (a fresh page load
// means no unsaved edits yet).
var billingDirty = false;
var _beforeUnloadBound = false;

function readJsonScript(id, fallback) {
    var node = document.getElementById(id);
    if (!node) return fallback;
    try {
        return JSON.parse(node.textContent);
    } catch {
        return fallback;
    }
}

function getCsrfToken(root) {
    var input = (root || document).querySelector('[name=csrfmiddlewaretoken]');
    if (input && input.value) return input.value;
    var match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
    return match ? match[1] : '';
}

function toast(message, type) {
    if (message && typeof window.showToast === 'function') {
        window.showToast(message, type || 'success');
    }
}

// ========================================================
// TOP-LEVEL SECTION TABS
// ========================================================

function initSettingsTabs(root) {
    var btns = root.querySelectorAll('[data-settings-tab]');
    var panels = root.querySelectorAll('[data-settings-panel]');
    var actions = root.querySelector('[data-settings-actions]');
    if (!btns.length || !panels.length) return;

    function activate(name) {
        if (SETTINGS_TABS.indexOf(name) === -1) name = 'documents';
        btns.forEach(function (b) {
            var on = b.dataset.settingsTab === name;
            b.className = 'settings-tab-btn px-4 py-2 text-sm font-medium border-b-2 ' +
                (on ? ACTIVE_TAB_CLASS : INACTIVE_TAB_CLASS);
        });
        panels.forEach(function (p) {
            p.classList.toggle('hidden', p.dataset.settingsPanel !== name);
        });
        // The Save / Cancel bar belongs to the main form; the Bank accounts
        // tab is a self-contained CRUD flow, so hide it there.
        if (actions) actions.classList.toggle('hidden', name === 'bank');
    }

    btns.forEach(function (b) {
        if (b._settingsTabBound) return;
        b._settingsTabBound = true;
        b.addEventListener('click', function () {
            var name = b.dataset.settingsTab;
            activate(name);
            try {
                var u = new URL(window.location.href);
                u.searchParams.set('tab', name);
                window.history.replaceState({}, '', u);
            } catch { /* history not available — ignore */ }
        });
    });

    var initial = 'documents';
    try {
        var qp = new URL(window.location.href).searchParams.get('tab');
        if (qp && SETTINGS_TABS.indexOf(qp) !== -1) initial = qp;
    } catch { /* ignore */ }

    // Open-on-error wins: a failed Save re-renders the form with field errors;
    // jump to whichever panel holds the first one so it's never hidden.
    var firstError = root.querySelector('[data-settings-panel] .field-error');
    if (firstError) {
        var panel = firstError.closest('[data-settings-panel]');
        if (panel && panel.dataset.settingsPanel) initial = panel.dataset.settingsPanel;
    }
    activate(initial);
}

// ========================================================
// PER-FIELD AUTO-SAVE (toggles + rounding-increment select)
// ========================================================

function lastGoodOf(el) {
    return el.type === 'checkbox' ? el.checked : el.value;
}

function revertField(el) {
    if (el.type === 'checkbox') {
        el.checked = !!el._lastGood;
    } else {
        el.value = el._lastGood;
    }
}

function flashSaved(el, message) {
    var group = el.closest('[data-autosave-group]');
    var status = group ? group.querySelector('[data-autosave-status]') : null;
    if (!status) return;
    status.textContent = '\u2713 ' + (message || '');
    status.classList.remove('opacity-0');
    status.classList.add('opacity-100');
    if (status._hideTimer) clearTimeout(status._hideTimer);
    status._hideTimer = setTimeout(function () {
        status.classList.remove('opacity-100');
        status.classList.add('opacity-0');
    }, 1800);
}

function saveField(el, url, okMsg, errMsg, root) {
    var body = new URLSearchParams();
    body.set('field', el.dataset.field);
    body.set('value', el.type === 'checkbox' ? (el.checked ? 'true' : 'false') : el.value);

    el.disabled = true;
    fetch(url, {
        method: 'POST',
        headers: {
            'X-CSRFToken': getCsrfToken(root),
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: body.toString(),
        credentials: 'same-origin',
    }).then(function (resp) {
        return resp.json().then(function (data) { return { ok: resp.ok, data: data }; });
    }).then(function (res) {
        if (res.ok && res.data && res.data.ok) {
            el._lastGood = lastGoodOf(el);
            flashSaved(el, okMsg);
        } else {
            revertField(el);
            toast((res.data && res.data.error) || errMsg, 'error');
        }
    }).catch(function () {
        revertField(el);
        toast(errMsg, 'error');
    }).then(function () {
        el.disabled = false;
    });
}

function initAutoSave(root) {
    var url = root.dataset.fieldUrl;
    if (!url) return;
    var okMsg = root.dataset.autosaveOk || '';
    var errMsg = root.dataset.autosaveError || '';

    root.querySelectorAll('[data-billing-autosave]').forEach(function (el) {
        el._lastGood = lastGoodOf(el);
        if (el._billingAutosaveBound) return;
        el._billingAutosaveBound = true;
        el.addEventListener('change', function () {
            saveField(el, url, okMsg, errMsg, root);
        });
    });
}

// ========================================================
// UNSAVED-CHANGES GUARD (explicit-save fields only)
// ========================================================

function bindBeforeUnloadOnce() {
    if (_beforeUnloadBound) return;
    _beforeUnloadBound = true;
    window.addEventListener('beforeunload', function (e) {
        if (typeof window._formDirty === 'function' && window._formDirty()) {
            e.preventDefault();
            e.returnValue = '';
        }
    });
}

function initDirtyGuard(root) {
    var form = root.querySelector('[data-billing-form]');
    if (!form) return;

    billingDirty = false;
    bindBeforeUnloadOnce();

    window._formDirtyConfig = {
        title: root.dataset.leaveTitle,
        message: root.dataset.leaveMessage,
        confirmText: root.dataset.leaveConfirm,
        cancelText: root.dataset.leaveCancel,
    };
    // Self-cleaning: once this form is swapped out of the DOM (boosted nav
    // away), the guard reports "not dirty" so it never blocks other pages.
    window._formDirty = function () {
        return billingDirty && document.body.contains(form);
    };

    // Server re-rendered with field errors => the operator's edits are unsaved.
    if (form.querySelector('.field-error')) billingDirty = true;

    if (form._billingDirtyBound) return;
    form._billingDirtyBound = true;

    var onEdit = function (e) {
        var t = e.target;
        // Auto-saving controls persist immediately — never count as "unsaved".
        if (t && t.closest && t.closest('[data-billing-autosave]')) return;
        billingDirty = true;
    };
    form.addEventListener('input', onEdit);
    form.addEventListener('change', onEdit);
    // Explicit Save = intentional persist. Clear in the capture phase so the
    // global htmx:confirm guard doesn't prompt on the Save round-trip.
    form.addEventListener('submit', function () { billingDirty = false; }, true);
}

// ========================================================
// EMAIL TEMPLATES (tabs + reset-to-default + lang autoload)
// ========================================================

// Grow a textarea to fit its content so the whole email is visible without
// an inner scrollbar. Skips hidden elements (a panel toggled with `hidden`
// reports scrollHeight 0, which would collapse it) — the caller re-runs this
// when a panel becomes visible. A floor keeps short/empty bodies from
// shrinking below the original 6-row look.
var EMAIL_BODY_MIN_HEIGHT = 150;

function autosizeTextarea(el) {
    if (!el || el.offsetParent === null) return;
    el.style.height = 'auto';
    el.style.height = Math.max(el.scrollHeight, EMAIL_BODY_MIN_HEIGHT) + 'px';
}

function initEmailTemplates(root) {
    var invoiceTemplates = readJsonScript('billing-invoice-templates', {});
    var cnTemplates = readJsonScript('billing-cn-templates', {});
    var reminderTemplates = readJsonScript('billing-reminder-templates', {});

    var langSelect = document.getElementById('id_billing_language');
    var loadBtn = document.getElementById('load-default-template-btn');

    var invSubject = document.getElementById('id_invoice_email_subject');
    var invBody = document.getElementById('id_invoice_email_body');
    var cnSubject = document.getElementById('id_credit_note_email_subject');
    var cnBody = document.getElementById('id_credit_note_email_body');
    var remSubject = document.getElementById('id_reminder_email_subject');
    var remBody = document.getElementById('id_reminder_email_body');

    var tabs = {
        invoice: {
            btn: document.getElementById('tab-btn-invoice'),
            panel: document.getElementById('tab-panel-invoice'),
        },
        'credit-note': {
            btn: document.getElementById('tab-btn-credit-note'),
            panel: document.getElementById('tab-panel-credit-note'),
        },
        reminder: {
            btn: document.getElementById('tab-btn-reminder'),
            panel: document.getElementById('tab-panel-reminder'),
        },
    };

    var bodies = [invBody, cnBody, remBody];

    function autosizeAll() {
        bodies.forEach(autosizeTextarea);
    }

    bodies.forEach(function (body) {
        if (!body) return;
        body.addEventListener('input', function () { autosizeTextarea(body); });
    });

    var activeTab = 'invoice';

    function switchTab(name) {
        if (!tabs[name]) return;
        activeTab = name;
        Object.keys(tabs).forEach(function (k) {
            var t = tabs[k];
            if (!t.btn || !t.panel) return;
            if (k === name) {
                t.panel.classList.remove('hidden');
                t.btn.className = 'email-tab-btn px-4 py-2 text-sm font-medium border-b-2 ' + ACTIVE_TAB_CLASS;
            } else {
                t.panel.classList.add('hidden');
                t.btn.className = 'email-tab-btn px-4 py-2 text-sm font-medium border-b-2 ' + INACTIVE_TAB_CLASS;
            }
        });
        // The newly-visible body now has a real scrollHeight — fit it.
        autosizeAll();
    }

    root.querySelectorAll('[data-billing-tab]').forEach(function (btn) {
        btn.addEventListener('click', function () {
            switchTab(btn.dataset.billingTab);
        });
    });

    // The whole Emails section starts hidden behind the top-level tab; resize
    // once it's revealed (click) and once now in case ?tab=emails opened it.
    var emailsTopBtn = root.querySelector('[data-settings-tab="emails"]');
    if (emailsTopBtn) {
        emailsTopBtn.addEventListener('click', function () {
            window.requestAnimationFrame(autosizeAll);
        });
    }
    window.requestAnimationFrame(autosizeAll);

    function getAllDefaults(templateSet, key) {
        var vals = [''];
        for (var lang in templateSet) {
            if (Object.prototype.hasOwnProperty.call(templateSet, lang)
                && templateSet[lang] && templateSet[lang][key]) {
                vals.push(templateSet[lang][key]);
            }
        }
        return vals;
    }

    function isDefaultTemplate(templateSet, subject, body) {
        if (!subject || !body) return true;
        var s = (subject.value || '').trim();
        var b = (body.value || '').trim();
        return getAllDefaults(templateSet, 'subject').indexOf(s) !== -1
            && getAllDefaults(templateSet, 'body').indexOf(b) !== -1;
    }

    function loadDefaults(templateSet, subject, body, lang) {
        var tpl = templateSet[lang] || templateSet['fr'];
        if (tpl && subject && body) {
            subject.value = tpl.subject;
            body.value = tpl.body;
            autosizeTextarea(body);
        }
    }

    if (langSelect) {
        langSelect.addEventListener('change', function () {
            var lang = this.value;
            if (isDefaultTemplate(invoiceTemplates, invSubject, invBody)) {
                loadDefaults(invoiceTemplates, invSubject, invBody, lang);
            }
            if (isDefaultTemplate(cnTemplates, cnSubject, cnBody)) {
                loadDefaults(cnTemplates, cnSubject, cnBody, lang);
            }
            if (isDefaultTemplate(reminderTemplates, remSubject, remBody)) {
                loadDefaults(reminderTemplates, remSubject, remBody, lang);
            }
        });
    }

    if (loadBtn) {
        loadBtn.addEventListener('click', function () {
            var lang = langSelect ? langSelect.value : 'fr';
            if (activeTab === 'invoice') {
                loadDefaults(invoiceTemplates, invSubject, invBody, lang);
            } else if (activeTab === 'credit-note') {
                loadDefaults(cnTemplates, cnSubject, cnBody, lang);
            } else {
                loadDefaults(reminderTemplates, remSubject, remBody, lang);
            }
        });
    }
}

// ========================================================
// PREFIX LIVE PREVIEWS
// ========================================================

function initPrefixPreviews(root) {
    var invInput = document.getElementById('id_invoice_prefix');
    var cnInput = document.getElementById('id_credit_note_prefix');
    var invPreview = document.getElementById('invoice-prefix-preview');
    var cnPreview = document.getElementById('cn-prefix-preview');
    if (!invInput || !cnInput) return;

    var year = new Date().getFullYear();
    var ds = root.dataset;
    var invFallback = ds.invoicePrefixFallback || '';
    var cnFallback = ds.cnPrefixFallback || '';

    function update() {
        var invVal = (invInput.value || '').trim().toUpperCase() || invFallback;
        var cnVal = (cnInput.value || '').trim().toUpperCase() || cnFallback;
        if (invPreview) invPreview.textContent = invVal + '-' + year + '-00001';
        if (cnPreview) cnPreview.textContent = cnVal + '-' + year + '-00001';
    }

    invInput.addEventListener('input', update);
    cnInput.addEventListener('input', update);
    update();
}

// Submit-button protection is handled globally by
// `frontend/src/utils/forms/submit-button.js` via `data-protect-submit`
// on the form root.

// ========================================================
// MAIN INIT
// ========================================================

export function initBillingSettings(config) {
    config = config || {};
    var root = config.root || document.querySelector('[data-auto-init="billingSettings"]');
    if (!root) return;
    initSettingsTabs(root);
    initAutoSave(root);
    initDirtyGuard(root);
    initEmailTemplates(root);
    initPrefixPreviews(root);
}

export function autoInitBillingSettings() {
    var root = document.querySelector('[data-auto-init="billingSettings"]');
    if (!root) return;
    initBillingSettings({ root: root });
}
